import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Users, AlertTriangle, Package, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { apiClient, LiveActivity } from '../api/client';

export default function LiveMonitor() {
  const [activity, setActivity] = useState<LiveActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchActivity = async () => {
    try {
      const data = await apiClient.getLiveActivity();
      setActivity(data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();

    // Refresh every 30 seconds
    intervalRef.current = setInterval(fetchActivity, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleRefresh = () => {
    setLoading(true);
    fetchActivity();
  };

  if (loading && !activity) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-64 bg-gray-200 rounded" />
            <div className="h-64 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="text-green-500" />
            Live Monitor
          </h1>
          <p className="text-sm text-gray-500">Locate2u app only. FedEx devices are not counted.</p>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
            <span className="text-sm text-gray-500">Live</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {lastUpdate && (
            <span className="text-sm text-gray-500">
              Last updated: {formatDistanceToNow(lastUpdate, { addSuffix: true })}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Active Users Card */}
      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="p-4 bg-green-100 rounded-full">
            <Users size={32} className="text-green-600" />
          </div>
          <div>
            <p className="text-4xl font-bold text-gray-900">{activity?.active_users || 0}</p>
            <p className="text-sm text-gray-500">Users online right now</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Recent Crashes */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="text-red-500" />
            Recent Crashes
          </h2>

          {!activity?.recent_crashes || activity.recent_crashes.length === 0 ? (
            <div className="text-center py-8">
              <AlertTriangle size={48} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">No recent crashes</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {activity.recent_crashes.map((crash) => (
                <Link
                  key={crash.id}
                  to={`/crashes/${crash.id}`}
                  className="block p-3 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-red-800 truncate">
                        {crash.error_message}
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        Device: {crash.device_id.slice(0, 8)}...
                      </p>
                    </div>
                    <span className="text-xs text-red-500 whitespace-nowrap ml-2">
                      {formatDistanceToNow(new Date(crash.occurred_at), { addSuffix: true })}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <Link
            to="/crashes"
            className="block mt-4 text-sm text-primary-600 hover:text-primary-700 text-center"
          >
            View all crashes →
          </Link>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="text-blue-500" />
            Recent Activity
          </h2>

          {!activity?.recent_activity || activity.recent_activity.length === 0 ? (
            <div className="text-center py-8">
              <Activity size={48} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">No recent activity</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {activity.recent_activity.map((event) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`badge ${
                        event.event_type === 'stop_completed'
                          ? 'badge-success'
                          : event.event_type === 'trip_started'
                          ? 'badge-info'
                          : event.event_type === 'trip_completed'
                          ? 'badge-success'
                          : 'badge-neutral'
                      }`}
                    >
                      {event.event_type.replace(/_/g, ' ')}
                    </span>
                    <Link
                      to={`/users/${event.device_id}`}
                      className="text-sm text-gray-600 hover:text-primary-600"
                    >
                      {event.device_id.slice(0, 8)}...
                    </Link>
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatDistanceToNow(new Date(event.occurred_at), { addSuffix: true })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="mt-6 text-center text-sm text-gray-500">
        <p>This page refreshes automatically every 30 seconds</p>
      </div>
    </div>
  );
}
